// Generated by Claude Code
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { PurchaseOrderAddressesContactSettings } from '#components/domain/operations/purchase-orders/settings/purchase-order-addresses-contact-settings.tsx'
import { PurchaseOrderGeneralSettings } from '#components/domain/operations/purchase-orders/settings/purchase-order-general-settings.tsx'
import { CustomFieldsEditor, type CustomFieldsAdapter } from '#components/domain/operations/settings/shared/custom-fields-editor.tsx'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import PurchaseOrdersApi from '#core/api/operations/purchase-orders/PurchaseOrders.ts'
import { useCanEditSetting } from '#core/entities/auth/roles/entity-settings-gates.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/purchase-orders')({
  component: RouteComponent,
})

const purchaseOrdersApi = new PurchaseOrdersApi()

const customFieldsAdapter: CustomFieldsAdapter = {
  load: () => purchaseOrdersApi.getCustomPropertiesDefinition(),
  save: (definition) => purchaseOrdersApi.upsertCustomPropertiesDefinition(definition),
}

const BILLING_FIELDS = [
  'billing_override_address',
  'billing_override_city',
  'billing_override_region',
  'billing_override_zipcode',
  'billing_override_country',
  'billing_override_additional_details',
]

/**
 * Purchase order form rows in drawer order. Vendor, shipping address and order items are
 * locked rows the backend excludes from form settings; `billing_address` stands for the six
 * `billing_override_*` fields.
 */
const PURCHASE_ORDER_FIELD_ORDER = [
  'vendor_id',
  'vendor_contact_id',
  'custom_purchase_number',
  'custom_vendor_number',
  'due_date',
  'work_order_id',
  'local_contact_id',
  'shipping_address',
  'billing_address',
  'description',
  'lines',
]

const PURCHASE_ORDER_LOCKED_FIELDS = ['vendor_id', 'shipping_address', 'lines']
const PURCHASE_ORDER_FIELD_GROUPS = { billing_address: BILLING_FIELDS }

type PurchaseOrdersSettingsTab = 'general' | 'addresses-contact' | 'form' | 'custom-fields'

/** Settings page for Purchase Orders: general settings, the addresses and contact a new order is prefilled with, the purchase order form and custom field definitions. */
function RouteComponent() {
  const { t } = useTranslation('purchase-orders')
  const { t: tGeneral } = useTranslation('general')
  const [activeTab, setActiveTab] = useState<PurchaseOrdersSettingsTab>('general')
  const userState = useUserState()
  const { showClose, handleClose } = useQuickSettingsReturn()

  const canEditGeneral = useCanEditSetting('purchase_order', 'default_due_date_offset_days')
  const canEditAddressesContact = useCanEditSetting('purchase_order', 'default_shipping_address')
  const canEditForm = userState.hasPermission(PermissionService.PURCHASE_ORDERS, PermissionAction.FORM_SETTINGS)
  const canManageCustomFields = userState.hasPermission(PermissionService.PURCHASE_ORDERS, PermissionAction.CUSTOM_PROPERTIES)

  const tabs = [
    canEditGeneral && { key: 'general' as const, label: t('shared.fields.general') },
    canEditAddressesContact && { key: 'addresses-contact' as const, label: t('settings.tabAddressesContact') },
    canEditForm && { key: 'form' as const, label: t('settings.tabForm') },
    canManageCustomFields && { key: 'custom-fields' as const, label: t('shared.fields.customFields') },
  ].filter(Boolean) as { key: PurchaseOrdersSettingsTab; label: string }[]

  const effectiveTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    vendor_id: t('shared.fields.vendor'),
    vendor_contact_id: t('settings.fieldVendorContact'),
    custom_purchase_number: t('settings.fieldCustomPurchaseNumber'),
    custom_vendor_number: t('settings.fieldCustomVendorNumber'),
    due_date: t('shared.fields.dueDate'),
    work_order_id: t('shared.fields.workOrder'),
    local_contact_id: t('settings.fieldDeliveryContact'),
    shipping_address: t('settings.fieldShippingAddress'),
    billing_address: t('settings.fieldBillingAddress'),
    description: t('shared.fields.additionalDetails'),
    lines: t('settings.fieldOrderItems'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <h1 className="h4 fw-bold mb-1">{t('settings.title')}</h1>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as PurchaseOrdersSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {effectiveTab === 'general' && <PurchaseOrderGeneralSettings />}

          {effectiveTab === 'addresses-contact' && <PurchaseOrderAddressesContactSettings />}

          {effectiveTab === 'form' && (
            <div>
              <p className="text-secondary mb-4">{t('settings.formDescription')}</p>
              <FormSettingsEditor
                entityType="purchase_order"
                fieldLabels={fieldLabels}
                orderedFields={PURCHASE_ORDER_FIELD_ORDER}
                alwaysVisibleFields={PURCHASE_ORDER_LOCKED_FIELDS}
                alwaysRequiredFields={PURCHASE_ORDER_LOCKED_FIELDS}
                fieldGroups={PURCHASE_ORDER_FIELD_GROUPS}
                requiredColumn={{
                  label: t('settings.columnMandatory'),
                  helpLabel: t('settings.columnMandatoryHelpLabel'),
                  help: t('settings.columnMandatoryHelp'),
                }}
                canEdit={canEditForm}
              />
            </div>
          )}

          {effectiveTab === 'custom-fields' && <CustomFieldsEditor adapter={customFieldsAdapter} canManage={canManageCustomFields} />}
        </>
      )}
    </div>
  )
}
